import React, { useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import {
  Avatar,
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  FormControl,
  TextField,
  Chip,
  InputAdornment,
  Stack,
  Tooltip,
  InputLabel,
  MenuItem,
  Select,
  Alert,
  Divider,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import {
  Add,
  Search,
  Visibility,
  VisibilityOff,
  PhotoCameraOutlined,
  ManageAccounts,
  LockReset,
  AlternateEmail,
  SearchRounded,
  RestartAltRounded,
  PictureAsPdfRounded,
  TableChartRounded,
} from "@mui/icons-material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { format } from "date-fns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";

import { DefaultPage } from "../../layouts/default-page";
import { Notify } from "../../layouts/notify";
import { SignUp } from "../../services/AuthService";
import {
  useAddAdminUserMutation,
  useGetAdminUsersQuery,
  useLazyGetAdminUsersQuery,
  useUpdateAdminUserMutation,
  useChangeDeputyPasswordMutation,
  useChangeUserEmailMutation,
} from "../../services/user.query";
import { DataTableFive } from "../../layouts/layout2/data-table-five";
import { StatusChip } from "../common/StatusChip";
import { RowActions } from "../common/RowActions";
import { MRT_SortingState } from "material-react-table";
import { ExportLoadingBackdrop } from "../common/ExportLoadingBackdrop";

type FormValues = {
  email: string;
  password: string;
  phone_number: string;
  name: string;
  last_name: string;
  status: string;
  profile_picture: string | null;
  profile_pic_file: File | null;
};

type PasswordFormValues = {
  new_password: string;
  confirm_password: string;
};

const getValidationSchema = (isEdit: boolean) =>
  Yup.object({
    name: Yup.string().required("First name is required"),
    last_name: Yup.string().required("Last name is required"),
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    password: isEdit
      ? Yup.string().notRequired()
      : Yup.string()
          .min(8, "Password must be at least 8 characters long")
          .required("Password is required"),
    phone_number: Yup.string()
      .nullable()
      .test(
        "digits-only",
        "Phone number must be only digits",
        (value) => !value || /^[0-9]+$/.test(value),
      ),
    status: Yup.string().required("Status is required"),

    profile_picture: Yup.string().nullable(),
    profile_pic_file: (isEdit
      ? Yup.mixed<File>().nullable()
      : Yup.mixed<File>().nullable()
    )
      .test(
        "fileType",
        "Only PNG, JPG, JPEG, and WEBP are allowed",
        (value) =>
          !value ||
          ["image/png", "image/jpeg", "image/jpg", "image/webp"].includes(
            value.type,
          ),
      )
      .test(
        "fileSize",
        "Image must not exceed 2MB",
        (value) => !value || value.size <= 2 * 1024 * 1024,
      ),
  });

const safeImageSrc = (value?: string | null) => {
  if (!value) return "";
  if (value.startsWith("blob:")) return "";
  return value;
};

export default function AdminUsersPage() {
  const [parentPage, setParentPage] = useState(1);
  const rowsPerPage = 100;

  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  
  const [sorting, setSorting] = useState<MRT_SortingState>([
    { id: "created_at", desc: true },
  ]);

  const sortColumn = sorting.length > 0 ? sorting[0].id : "created_at";
  const sortDirection = sorting.length > 0 ? (sorting[0].desc ? "desc" : "asc") : "desc";

  const mappedSortColumn = useMemo(() => {
    if (sortColumn === "last_name") return "LAST_NAME";
    if (sortColumn === "name") return "NAME";
    return "CREATED_AT";
  }, [sortColumn]);

  const [openAddModal, setOpenAddModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [showPassword, setShowPassword] = useState(false);

  const [openActionDialog, setOpenActionDialog] = useState(false);
  const [selectedActionUser, setSelectedActionUser] = useState<any>(null);

  const [openChangePasswordDialog, setOpenChangePasswordDialog] =
    useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [openChangeEmailDialog, setOpenChangeEmailDialog] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [confirmEmail, setConfirmEmail] = useState("");

  const [picPreviewUrl, setPicPreviewUrl] = useState("");
  const [picDragOver, setPicDragOver] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const { data, isLoading, isFetching, refetch } = useGetAdminUsersQuery({
    page: parentPage,
    rowsPerPage,
    searchTerm,
    sortBy: mappedSortColumn as any,
    sortDir: sortDirection,
  });

  const [addAdminUser, { isLoading: addUserLoading }] =
    useAddAdminUserMutation();

  const [updateAdminUser, { isLoading: updateUserLoading }] =
    useUpdateAdminUserMutation();

  const [changeDeputyPassword, { isLoading: changePasswordLoading }] =
    useChangeDeputyPasswordMutation();

  const [changeUserEmail, { isLoading: changingEmail }] =
    useChangeUserEmailMutation();

  const users = data?.list ?? [];
  const count = data?.count ?? 0;

  const [triggerFetchAllAdminUsers] = useLazyGetAdminUsersQuery();
  const [isExporting, setIsExporting] = useState(false);
  const [exportMessage, setExportMessage] = useState("");

  const handleSearch = () => {
    setParentPage(1);
    setSearchTerm(searchInput.trim());
  };

  const clearSearch = () => {
    setSearchInput("");
    setSearchTerm("");
    setParentPage(1);
  };

  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleExportPDF = async () => {
    setIsExporting(true);
    setExportMessage("Fetching all matching administrator accounts...");
    try {
      const result = await triggerFetchAllAdminUsers({
        page: 1,
        rowsPerPage: 999999,
        searchTerm,
      }).unwrap();

      const allRows = result?.list || [];
      if (allRows.length === 0) {
        Notify("info", "No records found to export.");
        return;
      }

      setExportMessage(`Preparing PDF document for all ${allRows.length.toLocaleString()} administrators...`);
      await new Promise((resolve) => setTimeout(resolve, 50));

      const doc = new jsPDF("landscape", "mm", "a4");
      doc.setFontSize(14);
      doc.setTextColor(0, 114, 188);
      doc.text("MTRCB - Administrator Accounts Directory", 14, 15);

      doc.setFontSize(9);
      doc.setTextColor(100, 116, 139);
      doc.text(
        `Generated on: ${format(new Date(), "MMMM dd, yyyy · hh:mm a")} | Total Administrators: ${allRows.length}`,
        14,
        22
      );

      const tableColumn = [
        "#",
        "Full Name",
        "Email Address",
        "Phone Number",
        "Role",
        "Status",
        "Registered At",
      ];

      const tableRows = allRows.map((item: any, idx: number) => [
        idx + 1,
        `${item.last_name || ""}, ${item.name || ""}`.trim() || "—",
        item.email || "—",
        item.phone_number || "—",
        item.role || "ADMIN",
        item.status === "VERIFIED" ? "ACTIVE" : item.status || "—",
        item.created_at ? format(new Date(item.created_at), "MM/dd/yyyy") : "—",
      ]);

      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 27,
        theme: "grid",
        headStyles: {
          fillColor: [0, 114, 188],
          textColor: [255, 255, 255],
          fontStyle: "bold",
          fontSize: 8.5,
        },
        alternateRowStyles: { fillColor: [248, 250, 252] },
        styles: {
          fontSize: 8,
          cellPadding: 2.5,
          textColor: [15, 23, 42],
          overflow: "linebreak",
        },
        margin: { top: 12, left: 14, right: 14, bottom: 12 },
        didDrawPage: (data: any) => {
          const pageCount = (doc as any).internal.getNumberOfPages();
          doc.setFontSize(7.5);
          doc.setTextColor(148, 163, 184);
          doc.text(
            `Page ${data.pageNumber} of ${pageCount}`,
            doc.internal.pageSize.getWidth() - 30,
            doc.internal.pageSize.getHeight() - 6
          );
        },
      });

      doc.save(`MTRCB_Administrators_${format(new Date(), "yyyyMMdd_HHmm")}.pdf`);
      Notify("success", `Exported all ${allRows.length} administrators to PDF.`);
    } catch (err: any) {
      console.error("PDF export error:", err);
      Notify("error", err?.message || "Failed to generate PDF export.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportExcel = async () => {
    setIsExporting(true);
    setExportMessage("Fetching all matching administrator accounts...");
    try {
      const result = await triggerFetchAllAdminUsers({
        page: 1,
        rowsPerPage: 999999,
        searchTerm,
      }).unwrap();

      const allRows = result?.list || [];
      if (allRows.length === 0) {
        Notify("info", "No records found to export.");
        return;
      }

      setExportMessage(`Generating Excel spreadsheet for all ${allRows.length.toLocaleString()} administrators...`);
      await new Promise((resolve) => setTimeout(resolve, 50));

      const rows = allRows.map((item: any, idx: number) => ({
        "No.": idx + 1,
        "Last Name": item.last_name || "—",
        "First Name": item.name || "—",
        "Email Address": item.email || "—",
        "Phone Number": item.phone_number || "—",
        "Role": item.role || "ADMIN",
        "Status": item.status === "VERIFIED" ? "ACTIVE" : item.status || "—",
        "Registered At": item.created_at ? format(new Date(item.created_at), "yyyy-MM-dd") : "—",
      }));

      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Administrators");

      ws["!cols"] = [
        { wch: 6 },
        { wch: 20 },
        { wch: 20 },
        { wch: 32 },
        { wch: 18 },
        { wch: 14 },
        { wch: 14 },
        { wch: 18 },
      ];

      XLSX.writeFile(wb, `MTRCB_Administrators_${format(new Date(), "yyyyMMdd_HHmm")}.xlsx`);
      Notify("success", `Exported all ${allRows.length} administrators to Excel.`);
    } catch (err: any) {
      console.error("Excel export error:", err);
      Notify("error", err?.message || "Failed to generate Excel export.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const openUserActionDialog = (user: any) => {
    setSelectedActionUser(user);
    setOpenActionDialog(true);
  };

  const closeUserActionDialog = () => {
    setOpenActionDialog(false);
    setSelectedActionUser(null);
  };

  const handleOpenUpdateFromAction = () => {
    const user = selectedActionUser;
    setOpenActionDialog(false);
    setSelectedActionUser(null);

    if (user) {
      openAddEditModal(user);
    }
  };

  const handleOpenChangePasswordFromAction = () => {
    setOpenActionDialog(false);
    setOpenChangePasswordDialog(true);
    passwordFormik.resetForm();
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  const handleOpenChangeEmailFromAction = () => {
    setOpenActionDialog(false);
    setNewEmail("");
    setConfirmEmail("");
    setOpenChangeEmailDialog(true);
  };

  const closeChangeEmailDialog = () => {
    if (changingEmail) return;
    setOpenChangeEmailDialog(false);
    setNewEmail("");
    setConfirmEmail("");
  };

  const submitChangeEmail = async () => {
    try {
      if (!selectedActionUser?.user_id) {
        Notify("error", "Missing Supabase Auth user_id for this admin.");
        return;
      }

      const emailTrimmed = newEmail.trim().toLowerCase();
      const confirmTrimmed = confirmEmail.trim().toLowerCase();

      if (!emailTrimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed)) {
        Notify("warning", "Please enter a valid email address.");
        return;
      }

      if (emailTrimmed !== confirmTrimmed) {
        Notify("warning", "Email confirmation does not match.");
        return;
      }

      if (emailTrimmed === (selectedActionUser?.email || "").trim().toLowerCase()) {
        Notify("warning", "The new email must be different from the current email.");
        return;
      }

      await changeUserEmail({
        user_id: selectedActionUser.user_id,
        new_email: emailTrimmed,
        users_id: selectedActionUser.id,
        is_deputy: false,
      }).unwrap();

      Notify("success", "Email changed successfully.");
      closeChangeEmailDialog();
      refetch();
    } catch (error: any) {
      console.error(error);
      Notify("error", error?.data || error?.message || "Failed to change email");
    }
  };

  const closeChangePasswordDialog = () => {
    setOpenChangePasswordDialog(false);
    setSelectedActionUser(null);
    passwordFormik.resetForm();
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  const closeAddEditModal = () => {
    setOpenAddModal(false);
    setEditMode(false);
    setCurrentUser(null);

    setPicPreviewUrl("");
    formik.resetForm();

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const openAddEditModal = (user: any = null) => {
    if (user) {
      setEditMode(true);
      setCurrentUser(user);

      const existingPicture = user?.profile_picture || "";

      formik.setValues({
        email: user?.email || "",
        password: "",
        phone_number: user?.phone_number || "",
        name: user?.name || "",
        last_name: user?.last_name || "",
        status: user?.status || "VERIFIED",

        profile_picture: existingPicture || null,
        profile_pic_file: null,
      });

      setPicPreviewUrl(existingPicture || "");
    } else {
      setEditMode(false);
      setCurrentUser(null);
      setPicPreviewUrl("");
      formik.resetForm();
    }

    setOpenAddModal(true);
  };

  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      email: "",
      password: "",
      phone_number: "",
      name: "",
      last_name: "",
      status: "VERIFIED",
      profile_picture: null,
      profile_pic_file: null,
    },
    validationSchema: getValidationSchema(editMode),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (editMode) {
          await updateAdminUser({
            id: currentUser?.id,
            name: values.name,
            last_name: values.last_name,
            phone_number: values.phone_number,
            status: values.status,
            profile_picture: values.profile_picture,
            profile_pic_file: values.profile_pic_file,
          }).unwrap();

          Notify("success", "Admin updated successfully");
        } else {
          const signUpResponse = await SignUp(values.email, values.password);

          if (!signUpResponse?.user?.id) {
            throw new Error("Failed to create auth account");
          }

          await addAdminUser({
            user_id: signUpResponse.user.id,
            email: values.email,
            phone_number: values.phone_number,
            name: values.name,
            last_name: values.last_name,
            role: "ADMIN",
            status: values.status,
            profile_picture: values.profile_picture,
            profile_pic_file: values.profile_pic_file,
          }).unwrap();

          Notify("success", "Admin added successfully");
        }

        closeAddEditModal();
        refetch();
      } catch (error: any) {
        console.error("Admin save error:", error);
        Notify("error", error?.data || error?.message || "Failed to save user");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const passwordFormik = useFormik<PasswordFormValues>({
    initialValues: {
      new_password: "",
      confirm_password: "",
    },
    validationSchema: Yup.object({
      new_password: Yup.string()
        .min(8, "Password must be at least 8 characters long")
        .required("New password is required"),
      confirm_password: Yup.string()
        .oneOf([Yup.ref("new_password")], "Passwords do not match")
        .required("Confirm password is required"),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const authUserId = selectedActionUser?.user_id;

        if (!authUserId) {
          throw new Error("This Admin user has no Supabase Auth user_id.");
        }

        await changeDeputyPassword({
          user_id: authUserId,
          password: values.new_password,
        }).unwrap();

        Notify("success", "Password changed successfully");
        closeChangePasswordDialog();
        refetch();
      } catch (error: any) {
        console.error("Admin change password error:", error);
        Notify(
          "error",
          error?.data || error?.message || "Failed to change password",
        );
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleSelectedImage = (file: File | null) => {
    if (!file) return;

    const allowedTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
    const maxSize = 2 * 1024 * 1024;

    if (!allowedTypes.includes(file.type)) {
      Notify("error", "Only PNG, JPG, JPEG, and WEBP are allowed");
      return;
    }

    if (file.size > maxSize) {
      Notify("error", "Image must not exceed 2MB");
      return;
    }

    const previewUrl = URL.createObjectURL(file);
    setPicPreviewUrl(previewUrl);
    formik.setFieldValue("profile_pic_file", file);
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    handleSelectedImage(file);
  };

  const handleDropImage = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setPicDragOver(false);

    const file = event.dataTransfer.files?.[0] || null;
    handleSelectedImage(file);
  };

  const handleRemoveImage = () => {
    setPicPreviewUrl("");
    formik.setFieldValue("profile_pic_file", null);
    formik.setFieldValue("profile_picture", null);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const columns = useMemo(
    () => [
      {
        id: "rowNumber",
        header: "No.",
        size: 70,
        enableSorting: false,
        Cell: ({ row }: any) => {
          const globalIndex = (parentPage - 1) * rowsPerPage + row.index + 1;

          return (
            <Typography>
              {globalIndex}
            </Typography>
          );
        },
      },
      {
        accessorKey: "user",
        header: "USER",
        id: "name",
        Cell: ({ row }: any) => {
          const fullName = `${row.original?.name || ""} ${
            row.original?.last_name || ""
          }`.trim();

          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Avatar
                src={safeImageSrc(row.original?.profile_picture)}
                alt={fullName}
                sx={{ width: 40, height: 40 }}
              >
                {row.original?.name?.[0] || "U"}
              </Avatar>

              <Box>
                <Typography fontWeight={700}>{fullName || "-"}</Typography>
              </Box>
            </Box>
          );
        },
      },
      {
        accessorKey: "email",
        header: "EMAIL",
        enableSorting: false,
      },
      {
        accessorKey: "phone_number",
        header: "PHONE NUMBER",
        enableSorting: false,
      },
      {
        accessorKey: "status",
        header: "STATUS",
        enableSorting: false,
        size: 130,
        Cell: ({ row }: any) => {
          const status = row.original?.status || "-";
          const label = status === "VERIFIED" ? "ACTIVE" : status;
          return (
            <StatusChip
              label={label}
              tone={label === "ACTIVE" ? "success" : "neutral"}
            />
          );
        },
      },
      {
        id: "actions",
        header: "ACTIONS",
        size: 190,
        enableSorting: false,
        Cell: ({ row }: any) => {
          const user = row.original;
          return (
            <RowActions
              actions={[
                {
                  label: "Edit",
                  onClick: () => openAddEditModal(user),
                },
                {
                  label: "Password",
                  onClick: () => {
                    setSelectedActionUser(user);
                    setOpenChangePasswordDialog(true);
                    passwordFormik.resetForm();
                    setShowNewPassword(false);
                    setShowConfirmPassword(false);
                  },
                },
                {
                  label: "Email",
                  onClick: () => {
                    setSelectedActionUser(user);
                    setNewEmail("");
                    setConfirmEmail("");
                    setOpenChangeEmailDialog(true);
                  },
                },
              ]}
              inline={2}
            />
          );
        },
      },
    ],
    [parentPage, rowsPerPage],
  );

  return (
    <DefaultPage 
      title="Admin Accounts" 
      subTitle="Manage administrator accounts and system access."
      loading={isLoading || isFetching}
    >
      <Box mb={2.5}>
        <Alert
          severity="info"
          sx={{
            borderRadius: "6px",
            fontSize: 13,
            border: "1px solid #BAE6FD",
            backgroundColor: "#F0F9FF",
            "& .MuiAlert-icon": { color: "#0072BC" },
          }}
        >
          Displays registered Admin users. Set the filters and click <strong>Search</strong> to load results.
        </Alert>
      </Box>

      {/* Standardized Gold-Standard Filter Card */}
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          border: "1px solid #E2E8F0",
          borderRadius: "6px",
          p: 2,
          mb: 2,
          boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.04)",
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={1.5}
            flexWrap="wrap"
            useFlexGap
            alignItems={{ xs: "stretch", md: "center" }}
          >
            <TextField
              size="small"
              label="Search Name, Email"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={onSearchKeyDown}
              sx={{
                minWidth: 320,
                "& .MuiOutlinedInput-root": { borderRadius: "4px", fontSize: 13.5 },
              }}
            />
          </Stack>

          <Divider sx={{ borderColor: "#F1F5F9" }} />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", sm: "center" }}
            spacing={1.5}
          >
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                size="small"
                startIcon={<SearchRounded fontSize="small" />}
                onClick={handleSearch}
                disabled={isFetching}
                sx={{
                  backgroundColor: "#0072BC",
                  color: "#FFFFFF",
                  fontWeight: 600,
                  fontSize: 13,
                  px: 2,
                  py: 0.75,
                  borderRadius: "4px",
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#005a96",
                    boxShadow: "none",
                  },
                }}
              >
                {isFetching ? "Searching..." : "Search"}
              </Button>

              {(searchTerm || searchInput) && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RestartAltRounded fontSize="small" />}
                  onClick={clearSearch}
                  disabled={isFetching}
                  sx={{
                    color: "#64748B",
                    borderColor: "#CBD5E1",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: 13,
                    borderRadius: "4px",
                    "&:hover": {
                      borderColor: "#94A3B8",
                      backgroundColor: "#F8FAFC",
                    },
                  }}
                >
                  Clear
                </Button>
              )}
            </Stack>

            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Button
                variant="outlined"
                size="small"
                startIcon={<PictureAsPdfRounded fontSize="small" />}
                onClick={handleExportPDF}
                disabled={isExporting}
                sx={{
                  color: "#334155",
                  borderColor: "#CBD5E1",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 13,
                  borderRadius: "4px",
                  "&:hover": {
                    borderColor: "#94A3B8",
                    backgroundColor: "#F8FAFC",
                  },
                }}
              >
                Export PDF
              </Button>

              <Button
                variant="outlined"
                size="small"
                startIcon={<TableChartRounded fontSize="small" />}
                onClick={handleExportExcel}
                disabled={isExporting}
                sx={{
                  color: "#0072BC",
                  borderColor: "#BAE6FD",
                  backgroundColor: "#F0F9FF",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 13,
                  borderRadius: "4px",
                  "&:hover": {
                    borderColor: "#0072BC",
                    backgroundColor: "#E0F2FE",
                  },
                }}
              >
                Export Excel
              </Button>

              <Button
                variant="contained"
                size="small"
                startIcon={<Add fontSize="small" />}
                onClick={() => openAddEditModal()}
                sx={{
                  backgroundColor: "#00934C",
                  color: "#FFFFFF",
                  fontWeight: 600,
                  fontSize: 13,
                  borderRadius: "4px",
                  textTransform: "none",
                  boxShadow: "none",
                  "&:hover": {
                    backgroundColor: "#007a3f",
                    boxShadow: "none",
                  },
                }}
              >
                Add Admin
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Box>

      {/* Matched Records Banner */}
      <Box mb={2}>
        <Alert
          severity="success"
          sx={{
            borderRadius: "6px",
            fontSize: 13,
            border: "1px solid #BBF7D0",
            backgroundColor: "#F0FDF4",
            py: 0.5,
            "& .MuiAlert-icon": { color: "#00934C" },
          }}
        >
          Total matched records: <strong>{Number(count || 0).toLocaleString()}</strong>
        </Alert>
      </Box>

      <ExportLoadingBackdrop open={isExporting} message={exportMessage} />

      <DataTableFive
        data={users}
        total={count}
        page={parentPage}
        pageSize={rowsPerPage}
        onPageChange={setParentPage}
        columns={columns as any}
        getRowId={(row: any) => String(row.id)}
        isLoading={isLoading}
        isFetching={isFetching}
        onRowClick={(row: any) => openAddEditModal(row.original)}
        sorting={sorting}
        onSortingChange={(s) => {
          setSorting(s);
          setParentPage(1);
        }}
      />

      {/* Action Dialog */}
      <Dialog
        open={openActionDialog}
        onClose={closeUserActionDialog}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: "hidden",
          },
        }}
      >
        <Box sx={{ position: "relative", p: 2.5, pb: 1 }}>
          <IconButton
            aria-label="close"
            onClick={closeUserActionDialog}
            sx={{ position: "absolute", top: 8, right: 8, p: 0 }}
          >
            <img alt="close icon" src="/x.png" />
          </IconButton>

          <Typography variant="h6" fontWeight={700}>
            Select Action
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {selectedActionUser
              ? `${selectedActionUser.last_name ?? ""}, ${
                  selectedActionUser.name ?? ""
                }`
              : "Admin User"}
          </Typography>
        </Box>

        <DialogContent dividers sx={{ p: 2.5 }}>
          <Stack spacing={1.5}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<ManageAccounts />}
              onClick={handleOpenUpdateFromAction}
              sx={{
                justifyContent: "flex-start",
                py: 1.25,
                borderRadius: "6px",
                textTransform: "none",
                fontWeight: 700,
              }}
            >
              Update Admin
            </Button>

            <Button
              fullWidth
              variant="contained"
              startIcon={<LockReset />}
              onClick={handleOpenChangePasswordFromAction}
              sx={{
                justifyContent: "flex-start",
                py: 1.25,
                borderRadius: "6px",
                textTransform: "none",
                fontWeight: 700,
              }}
            >
              Change Password
            </Button>

            <Button
              fullWidth
              variant="contained"
              startIcon={<AlternateEmail />}
              onClick={handleOpenChangeEmailFromAction}
              sx={{
                justifyContent: "flex-start",
                py: 1.25,
                borderRadius: "6px",
                textTransform: "none",
                fontWeight: 700,
                bgcolor: "#2e7d32",
                "&:hover": { bgcolor: "#1b5e20" },
              }}
            >
              Change Email
            </Button>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* Add / Edit Admin Dialog */}
      <Dialog
        open={openAddModal}
        onClose={closeAddEditModal}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <form onSubmit={formik.handleSubmit}>
          <Box sx={{ position: "relative", p: 2.5, pb: 1 }}>
            <IconButton
              onClick={closeAddEditModal}
              sx={{ position: "absolute", top: 12, right: 12 }}
            >
              <img alt="close icon" src="/x.png" />
            </IconButton>

            <Typography variant="h6" fontWeight={700}>
              {editMode ? "Update Admin Account" : "Add Admin Account"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {editMode
                ? "Update the details for this Admin account."
                : "Create a new Admin account by filling in the details below."}
            </Typography>
          </Box>

          <DialogContent dividers sx={{ p: 3 }}>
            <Stack spacing={3}>
              <Box>
                <Typography fontSize={14} fontWeight={600} mb={1}>
                  Profile Picture
                </Typography>
                <input
                  type="file"
                  accept="image/png, image/jpeg, image/jpg, image/webp"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleImageChange}
                />
                <Box
                  onDragOver={(e) => {
                    e.preventDefault();
                    setPicDragOver(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    setPicDragOver(false);
                  }}
                  onDrop={handleDropImage}
                  sx={{
                    width: 140,
                    height: 140,
                    borderRadius: "50%",
                    border: "2px dashed",
                    borderColor: picDragOver
                      ? "primary.main"
                      : formik.touched.profile_pic_file &&
                        formik.errors.profile_pic_file
                      ? "error.main"
                      : "#ccc",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    overflow: "hidden",
                    bgcolor: picDragOver ? "rgba(25, 118, 210, 0.05)" : "#f9f9f9",
                    cursor: "pointer",
                    "&:hover": { borderColor: "primary.main" },
                    mx: "auto",
                  }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {picPreviewUrl ? (
                    <Box
                      component="img"
                      src={picPreviewUrl}
                      sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <Stack alignItems="center" spacing={0.5} color="text.secondary">
                      <PhotoCameraOutlined sx={{ fontSize: 32 }} />
                      <Typography fontSize={12} textAlign="center" px={2}>
                        Click or Drop <br /> image here
                      </Typography>
                    </Stack>
                  )}
                </Box>
                <Box display="flex" justifyContent="center" mt={1}>
                  {picPreviewUrl && (
                    <Button
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRemoveImage();
                      }}
                      sx={{ textTransform: "none" }}
                    >
                      Remove Picture
                    </Button>
                  )}
                </Box>
                {formik.touched.profile_pic_file &&
                  formik.errors.profile_pic_file && (
                    <Typography
                      color="error"
                      fontSize={12}
                      textAlign="center"
                      mt={0.5}
                    >
                      {String(formik.errors.profile_pic_file)}
                    </Typography>
                  )}
              </Box>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                />
                <TextField
                  fullWidth
                  label="Last Name"
                  name="last_name"
                  value={formik.values.last_name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.last_name && Boolean(formik.errors.last_name)
                  }
                  helperText={
                    formik.touched.last_name && formik.errors.last_name
                  }
                />
              </Stack>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  disabled={editMode}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                />

                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phone_number"
                  value={formik.values.phone_number}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.phone_number &&
                    Boolean(formik.errors.phone_number)
                  }
                  helperText={
                    formik.touched.phone_number && formik.errors.phone_number
                  }
                />
              </Stack>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                {!editMode && (
                  <TextField
                    fullWidth
                    label="Password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.password && Boolean(formik.errors.password)
                    }
                    helperText={
                      formik.touched.password && formik.errors.password
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={handleTogglePasswordVisibility}>
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}

                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    label="Status"
                    value={formik.values.status}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  >
                    <MenuItem value="VERIFIED">ACTIVE</MenuItem>
                    <MenuItem value="BLOCKED">BLOCKED</MenuItem>
                    <MenuItem value="PENDING">PENDING</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </Stack>
          </DialogContent>

          <DialogActions sx={{ p: 2.5 }}>
            <Button
              onClick={closeAddEditModal}
              disabled={addUserLoading || updateUserLoading}
              sx={{ color: "text.secondary", fontWeight: 700 }}
            >
              Cancel
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={addUserLoading || updateUserLoading}
              sx={{ fontWeight: 700 }}
            >
              {editMode ? "Update Admin" : "Save Admin"}
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog
        open={openChangePasswordDialog}
        onClose={closeChangePasswordDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <form onSubmit={passwordFormik.handleSubmit}>
          <Box sx={{ position: "relative", p: 2.5, pb: 1 }}>
            <IconButton
              onClick={closeChangePasswordDialog}
              sx={{ position: "absolute", top: 12, right: 12 }}
            >
              <img alt="close icon" src="/x.png" />
            </IconButton>

            <Typography variant="h6" fontWeight={700}>
              Change Password
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Set a new password for{" "}
              <strong>
                {selectedActionUser?.last_name},{" "}
                {selectedActionUser?.name}
              </strong>
            </Typography>
          </Box>

          <DialogContent dividers sx={{ p: 3 }}>
            <Stack spacing={3}>
              <TextField
                fullWidth
                label="New Password"
                name="new_password"
                type={showNewPassword ? "text" : "password"}
                value={passwordFormik.values.new_password}
                onChange={passwordFormik.handleChange}
                onBlur={passwordFormik.handleBlur}
                error={
                  passwordFormik.touched.new_password &&
                  Boolean(passwordFormik.errors.new_password)
                }
                helperText={
                  passwordFormik.touched.new_password &&
                  passwordFormik.errors.new_password
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowNewPassword(!showNewPassword)}
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label="Confirm Password"
                name="confirm_password"
                type={showConfirmPassword ? "text" : "password"}
                value={passwordFormik.values.confirm_password}
                onChange={passwordFormik.handleChange}
                onBlur={passwordFormik.handleBlur}
                error={
                  passwordFormik.touched.confirm_password &&
                  Boolean(passwordFormik.errors.confirm_password)
                }
                helperText={
                  passwordFormik.touched.confirm_password &&
                  passwordFormik.errors.confirm_password
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowConfirmPassword(!showConfirmPassword)
                        }
                      >
                        {showConfirmPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
          </DialogContent>

          <DialogActions sx={{ p: 2.5 }}>
            <Button
              onClick={closeChangePasswordDialog}
              disabled={changePasswordLoading}
              sx={{ color: "text.secondary", fontWeight: 700 }}
            >
              Cancel
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={changePasswordLoading}
              sx={{ fontWeight: 700 }}
            >
              Update Password
            </LoadingButton>
          </DialogActions>
        </form>
      </Dialog>

      {/* Change Email Dialog */}
      <Dialog
        open={openChangeEmailDialog}
        onClose={closeChangeEmailDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: { borderRadius: 2 },
        }}
      >
        <Box sx={{ position: "relative", p: 2.5, pb: 1 }}>
          <IconButton
            onClick={closeChangeEmailDialog}
            disabled={changingEmail}
            sx={{ position: "absolute", top: 12, right: 12 }}
          >
            <img alt="close icon" src="/x.png" />
          </IconButton>

          <Typography variant="h6" fontWeight={700}>
            Change Email
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Set a new email for{" "}
            <strong>
              {selectedActionUser?.last_name},{" "}
              {selectedActionUser?.name}
            </strong>
          </Typography>
        </Box>

        <DialogContent dividers sx={{ p: 3 }}>
          <Stack spacing={3}>
            {selectedActionUser?.email && (
              <Box sx={{ p: 1.5, bgcolor: "action.hover", borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Current Email
                </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {selectedActionUser.email}
                </Typography>
              </Box>
            )}
            <TextField
              fullWidth
              label="New Email Address"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              disabled={changingEmail}
              helperText="Enter the new email address"
            />
            <TextField
              fullWidth
              label="Confirm Email Address"
              type="email"
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              disabled={changingEmail}
              onKeyDown={(e) => {
                if (e.key === "Enter") submitChangeEmail();
              }}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2.5 }}>
          <Button
            onClick={closeChangeEmailDialog}
            disabled={changingEmail}
            sx={{ color: "text.secondary", fontWeight: 700 }}
          >
            Cancel
          </Button>
          <LoadingButton
            variant="contained"
            loading={changingEmail}
            onClick={submitChangeEmail}
            sx={{ fontWeight: 700, bgcolor: "#2e7d32", "&:hover": { bgcolor: "#1b5e20" } }}
          >
            Update Email
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </DefaultPage>
  );
}
